import ELK from "elkjs/lib/elk.bundled.js";
import { err, ok } from "../result.js";
import { isTupleVariantFields, } from "../model/types.js";
import { formatVariantName } from "../variant.js";
import { measureText } from "./measure.js";
const DEFAULT_FONT_SIZE = 13;
const DEFAULT_PAD_X = 12;
const DEFAULT_PAD_Y = 6;
const HEADER_PAD_Y = 8;
/** [RENDER-UNION-ONEOF] Extra height for the "one of" badge below union headers. */
const UNION_BADGE_H = 16;
function rowText(name, type) {
    return `${name}: ${printRefShort(type)}`;
}
function printRefShort(t) {
    if (t.args.length === 0) {
        return t.name;
    }
    return `${t.name}<${t.args.map(printRefShort).join(", ")}>`;
}
function declHeader(d) {
    const generics = d.generics.length === 0 ? "" : `<${d.generics.join(", ")}>`;
    const tag = d.kind === "record" ? "" : d.kind === "union" ? "union " : "alias ";
    return `${tag}${d.name}${generics}`;
}
function buildPreNodes(decls, fontSize, padX, padY) {
    const rowH = fontSize * 1.4 + padY * 0.5;
    const headerH = fontSize * 1.4 + HEADER_PAD_Y * 2;
    const out = [];
    for (const d of decls) {
        const header = declHeader(d);
        const headerSize = measureText(header, fontSize);
        let widest = headerSize.w;
        const rows = [];
        /** [RENDER-UNION-ONEOF] Union nodes get extra badge space below the header. */
        let y = d.kind === "union" ? headerH + UNION_BADGE_H : headerH;
        if (d.kind === "record") {
            for (const f of d.fields) {
                const text = rowText(f.name, f.type);
                const m = measureText(text, fontSize);
                if (m.w > widest) {
                    widest = m.w;
                }
                rows.push({ text, y, height: rowH });
                y += rowH;
            }
        }
        else if (d.kind === "union") {
            for (const v of d.variants) {
                const head = formatVariantName(v.name, v.discriminant);
                const variantHeader = v.fields.length === 0
                    ? head
                    : isTupleVariantFields(v.fields)
                        ? `${head}(${v.fields.map((f) => printRefShort(f.type)).join(", ")})`
                        : `${head} { ${v.fields.map((f) => rowText(f.name, f.type)).join(", ")} }`;
                const m = measureText(variantHeader, fontSize);
                if (m.w > widest) {
                    widest = m.w;
                }
                rows.push({ text: variantHeader, y, height: rowH });
                y += rowH;
            }
        }
        else {
            const text = `= ${printRefShort(d.target)}`;
            const m = measureText(text, fontSize);
            if (m.w > widest) {
                widest = m.w;
            }
            rows.push({ text, y, height: rowH });
            y += rowH;
        }
        const width = Math.ceil(widest + padX * 2);
        const height = Math.ceil(y);
        out.push({ id: d.name, decl: d, header, rows, width, height });
    }
    return out;
}
function rowPortId(nodeId, rowIndex) {
    return `${nodeId}::row::${String(rowIndex)}`;
}
function headerPortId(nodeId) {
    return `${nodeId}::header`;
}
function edgeContentId(e) {
    return `${e.sourceDeclName}:${String(e.sourceRowIndex)}:${e.targetDeclName}:${e.kind}`;
}
function edgePortIds(e) {
    const sourcePort = e.sourceRowIndex < 0 ? headerPortId(e.sourceDeclName) : rowPortId(e.sourceDeclName, e.sourceRowIndex);
    const targetPort = headerPortId(e.targetDeclName);
    return { sourcePort, targetPort };
}
function buildElkGraph(model, pre) {
    const nodeOptions = {
        "elk.portConstraints": "FIXED_SIDE",
    };
    const children = pre.map((n) => {
        const ports = [];
        ports.push({
            id: headerPortId(n.id),
            width: 1,
            height: 1,
            layoutOptions: { "elk.port.side": "WEST" },
            properties: { "port.index": "0" },
        });
        n.rows.forEach((_r, i) => {
            ports.push({
                id: rowPortId(n.id, i),
                width: 1,
                height: 1,
                layoutOptions: { "elk.port.side": "EAST" },
            });
        });
        return { id: n.id, width: n.width, height: n.height, ports, layoutOptions: nodeOptions };
    });
    const edgeMap = new Map();
    const edges = model.edges.map((e) => {
        const id = edgeContentId(e);
        edgeMap.set(id, e);
        const { sourcePort, targetPort } = edgePortIds(e);
        return { id, sources: [sourcePort], targets: [targetPort] };
    });
    const graph = {
        id: "root",
        layoutOptions: {
            "elk.algorithm": "layered",
            "elk.direction": "RIGHT",
            "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
            "elk.edgeRouting": "ORTHOGONAL",
            "elk.spacing.nodeNode": "40",
            "elk.layered.spacing.nodeNodeBetweenLayers": "60",
            "elk.spacing.edgeNode": "20",
        },
        children,
        edges,
    };
    return { graph, edgeMap };
}
function projectResult(pre, edgeMap, elkResult) {
    const preById = new Map(pre.map((n) => [n.id, n]));
    const nodes = (elkResult.children ?? []).flatMap((c) => {
        const p = preById.get(c.id);
        if (p === undefined) {
            return [];
        }
        return [
            {
                id: p.id,
                declName: p.decl.name,
                declKind: p.decl.kind,
                x: c.x ?? 0,
                y: c.y ?? 0,
                width: c.width ?? p.width,
                height: c.height ?? p.height,
                header: p.header,
                rows: p.rows.map((r) => ({ ...r })),
            },
        ];
    });
    const edges = (elkResult.edges ?? []).flatMap((e) => {
        const modelEdge = edgeMap.get(e.id);
        if (modelEdge === undefined) {
            return [];
        }
        const section = e.sections?.[0];
        const points = section ? [section.startPoint, ...(section.bendPoints ?? []), section.endPoint] : [];
        return [
            {
                id: e.id,
                sourceNodeId: modelEdge.sourceDeclName,
                targetNodeId: modelEdge.targetDeclName,
                points,
                label: modelEdge.label,
                kind: modelEdge.kind,
            },
        ];
    });
    return { width: elkResult.width ?? 0, height: elkResult.height ?? 0, nodes, edges };
}
let elkInstance = null;
function getElk() {
    elkInstance ??= new ELK();
    return elkInstance;
}
let warmedUp = false;
export async function warmupLayout() {
    if (warmedUp) {
        return;
    }
    const trivial = {
        id: "__warmup",
        layoutOptions: { "elk.algorithm": "layered", "elk.direction": "RIGHT" },
        children: [
            { id: "a", width: 1, height: 1 },
            { id: "b", width: 1, height: 1 },
        ],
        edges: [{ id: "e", sources: ["a"], targets: ["b"] }],
    };
    await getElk().layout(trivial);
    warmedUp = true;
}
export function isLayoutWarm() {
    return warmedUp;
}
function callElkSync(graph) {
    const elk = getElk();
    const DispatcherCtor = elk.worker.worker.dispatcher.constructor;
    const answers = [];
    const dispatcher = new DispatcherCtor({ postMessage: (m) => answers.push(m) });
    const graphCopy = JSON.parse(JSON.stringify(graph));
    dispatcher.saveDispatch({ data: { id: 0, cmd: "layout", graph: graphCopy, layoutOptions: {}, options: {} } });
    const answer = answers[0];
    if (answer === undefined) {
        throw new Error("elk sync dispatcher returned no answer");
    }
    if (answer.error) {
        throw new Error(answer.error.message ?? "elk sync error");
    }
    if (!answer.data) {
        throw new Error("elk sync dispatcher returned no data");
    }
    return answer.data;
}
// [LAYOUT-SHARED] Produces the ElkGraph + projection pieces used by BOTH sync and async.
function prepare(model, opts) {
    const fontSize = opts.fontSize ?? DEFAULT_FONT_SIZE;
    const padX = opts.rowPaddingX ?? DEFAULT_PAD_X;
    const padY = opts.rowPaddingY ?? DEFAULT_PAD_Y;
    const pre = buildPreNodes(model.decls, fontSize, padX, padY);
    const { graph, edgeMap } = buildElkGraph(model, pre);
    return { pre, graph, edgeMap };
}
function layoutError(e) {
    return err([
        {
            severity: "error",
            message: `layout failed: ${e.message}`,
            line: 0,
            col: 0,
            length: 0,
        },
    ]);
}
export async function layout(model, opts = {}) {
    const { pre, graph, edgeMap } = prepare(model, opts);
    try {
        const result = (await getElk().layout(graph));
        warmedUp = true;
        return ok(projectResult(pre, edgeMap, result));
    }
    catch (e) {
        return layoutError(e);
    }
}
// [LAYOUT-SYNC] Synchronous layout. Caller MUST have awaited `warmupLayout()` (or any
// async `layout()` call) at least once before invoking this.
export function layoutSync(model, opts = {}) {
    if (!warmedUp) {
        return err([
            {
                severity: "error",
                message: "layoutSync called before warmup; call warmupLayout() first",
                line: 0,
                col: 0,
                length: 0,
            },
        ]);
    }
    const { pre, graph, edgeMap } = prepare(model, opts);
    try {
        const result = callElkSync(graph);
        return ok(projectResult(pre, edgeMap, result));
    }
    catch (e) {
        return layoutError(e);
    }
}
//# sourceMappingURL=elk.js.map